import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays, CalendarPlus, MapPin, Search, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, CATEGORY_COLORS, DAYS, SCHEDULE, VENUES } from "@/data/content";
import { Reveal, SectionHead } from "@/components/Reveal";
import { downloadIcs } from "@/lib/calendar";

const MAP_VENUES = VENUES.map((v) => v.name);

export default function Schedule({ venue, onSelectVenue, onClearVenue }) {
  const [day, setDay] = useState("1");
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  const events = useMemo(() => {
    const query = q.trim().toLowerCase();
    return SCHEDULE.filter((e) => {
      if (!venue && e.day !== day) return false;
      if (cat !== "All" && e.category !== cat) return false;
      if (venue && e.venue !== venue) return false;
      if (query) {
        const hay = `${e.title} ${e.venue} ${e.desc} ${e.time}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [day, cat, q, venue]);

  return (
    <section id="schedule" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHead
          eyebrow="Programme of Events"
          title="Three Days at Chandbagh"
          sub="Filter by day and category, or search for an event, timing or venue — from the Rose Bowl to Skinners."
        />

        <AnimatePresence>
          {venue && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mx-auto mb-8 flex max-w-fit items-center gap-3 rounded-full border border-gold/40 bg-gold/10 px-5 py-2.5"
              data-testid="schedule-venue-chip"
            >
              <MapPin className="h-4 w-4 text-gold" />
              <span className="text-sm text-fg">Showing events at <span className="font-semibold text-gold">{venue}</span></span>
              <button onClick={onClearVenue} data-testid="schedule-venue-clear" className="flex h-6 w-6 items-center justify-center rounded-full text-soft transition-colors hover:bg-gold/20 hover:text-gold" aria-label="Clear venue filter">
                <X className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-8 flex flex-wrap items-center justify-center gap-2" data-testid="schedule-day-tabs">
          {DAYS.map((d) => (
            <motion.button
              key={d.id}
              layout
              onClick={() => { setDay(d.id); if (venue) onClearVenue(); }}
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              data-testid={`schedule-day-${d.id}`}
              className={`rounded-full px-6 py-3 text-left transition-colors duration-300 ${!venue && day === d.id ? "bg-gold text-navy shadow-[0_0_30px_rgba(212,175,55,0.3)]" : "glass text-fg hover:border-gold/40"}`}
            >
              <span className="block font-display text-sm font-bold tracking-widest">{d.label.toUpperCase()}</span>
              <span className={`block text-[10px] tracking-wide ${!venue && day === d.id ? "text-navy/70" : "text-soft"}`}>{d.date} · {d.tag}</span>
            </motion.button>
          ))}
        </div>

        <Reveal className="mb-10 flex flex-col items-center justify-between gap-4 lg:flex-row">
          <div className="flex flex-wrap items-center justify-center gap-2" data-testid="schedule-category-filters">
            {CATEGORIES.map((c) => (
              <motion.button
                key={c}
                layout
                onClick={() => setCat(c)}
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.94 }}
                transition={{ type: "spring", stiffness: 320, damping: 18 }}
                data-testid={`schedule-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-xs font-medium tracking-wide transition-colors duration-300 ${cat === c ? "border border-gold/60 bg-gold/15 text-gold" : "glass text-soft hover:text-fg"}`}
              >
                {c !== "All" && <span className="mr-2 inline-block h-2 w-2 rounded-full align-middle" style={{ background: CATEGORY_COLORS[c] }} />}
                {c}
              </motion.button>
            ))}
          </div>
          <div className="glass flex w-full items-center gap-3 rounded-full px-5 py-3 lg:w-80" data-testid="schedule-search-wrapper">
            <Search className="h-4 w-4 shrink-0 text-gold" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search events, venues, timings…"
              data-testid="schedule-search-input"
              className="w-full bg-transparent text-sm text-fg outline-none placeholder:text-soft/70"
            />
            {q && (
              <button onClick={() => setQ("")} aria-label="Clear search" data-testid="schedule-search-clear" className="text-soft hover:text-gold">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </Reveal>

        <motion.div layout className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="schedule-grid">
          <AnimatePresence mode="popLayout">
            {events.map((e) => {
              const isMapVenue = MAP_VENUES.includes(e.venue);
              const active = venue === e.venue;
              return (
                <motion.article
                  layout
                  key={e.id}
                  initial={{ opacity: 0, y: 24, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`schedule-event-${e.id}`}
                  className="glass group flex flex-col rounded-3xl p-6 transition-colors duration-500 hover:border-gold/40 hover:shadow-[0_18px_50px_rgba(0,0,0,0.3)]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 font-mono text-xs tracking-widest text-gold">
                      <CalendarDays className="h-3.5 w-3.5" /> {e.time}
                      {venue && <span className="text-soft">· {DAYS.find((d) => d.id === e.day)?.label}</span>}
                    </span>
                    <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-soft">
                      <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[e.category] }} />
                      {e.category}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-lg font-bold leading-snug text-fg transition-colors group-hover:text-gold">{e.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-soft">{e.desc}</p>
                  <div className="mt-5 flex w-full items-center justify-between gap-3">
                    {isMapVenue ? (
                      <button
                        onClick={() => onSelectVenue(active ? null : e.venue)}
                        data-testid={`schedule-venue-${e.id}`}
                        className={`flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs tracking-wide transition-all duration-300 ${active ? "bg-gold/20 text-gold" : "border border-line/20 text-soft hover:border-gold/50 hover:text-gold"}`}
                      >
                        <MapPin className="h-3.5 w-3.5" /> {e.venue}
                      </button>
                    ) : (
                      <span className="flex w-fit items-center gap-2 text-xs tracking-wide text-soft">
                        <MapPin className="h-3.5 w-3.5" /> {e.venue}
                      </span>
                    )}
                    <button
                      onClick={() => { downloadIcs(e); toast.success("Calendar invite downloaded — see you at Chandbagh"); }}
                      data-testid={`event-ics-${e.id}`}
                      aria-label={`Add ${e.title} to calendar`}
                      title="Add to calendar (.ics)"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line/25 text-soft transition-all duration-300 hover:border-gold/50 hover:text-gold"
                    >
                      <CalendarPlus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {events.length === 0 && (
          <div className="glass mx-auto mt-6 flex max-w-md flex-col items-center rounded-3xl p-10 text-center" data-testid="schedule-empty-state">
            <SlidersHorizontal className="h-8 w-8 text-gold" />
            <p className="mt-4 text-soft">Nothing matches that combination — try another day, filter or search.</p>
            <button
              onClick={() => { setCat("All"); setQ(""); onClearVenue(); }}
              data-testid="schedule-reset"
              className="mt-5 rounded-full bg-gold px-6 py-2.5 text-xs font-bold tracking-widest text-navy transition-transform hover:scale-105"
            >
              RESET FILTERS
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
